<script setup>
import { useVideoCallState } from "@/stores/videoCallState";
import PeerVideo from "./PeerVideo.vue";
import { computed } from "vue";
import { ref } from "vue";
import { onUnmounted } from "vue";
import { onMounted } from "vue";
import { EventManager, Events } from "@events";
const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  hideLocalVideo: {
    type: Boolean,
    default: false,
  },
  disableVideoMutedIcon: {
    type: Boolean,
    default: false,
  },
});

function isPeerHidden(peer) {
  if (!peer) {
    return true;
  }
  if (peer.type == "local" && props.hideLocalVideo) {
    return true;
  }
  return false;
}

const state = useVideoCallState(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);

const windowSize = ref({
  width: window.innerWidth,
  height: window.innerHeight,
});

// calculate flex direction based on window size and video resolutions
// calculates the max possible area for each peer's video element
// and returns the flex direction that has the largest total area
const flexDirection = computed(() => {
  // get each peer's video element srcObject and collect video resolutions

  const videoResolutions = Object.values(state.peers)
    .map((peer) => {
      // if screen size is bigger than 600 and the peer is local, return null
      // this is to prevent the local video from being included in the calculation
      if (windowSize.value.width > 800 && peer.type == "local") {
        return;
      }

      if (peer.video && peer.video.srcObject) {
        const videoTrack = peer.video.srcObject.getVideoTracks()[0];
        if (videoTrack) {
          return {
            width: videoTrack.getSettings().width,
            height: videoTrack.getSettings().height,
          };
        }
      }
    })
    .filter((resolution) => resolution);

  // if there are no video resolutions, return default flex direction
  if (videoResolutions.length == 0 || videoResolutions.length == 1) {
    return "row";
  }

  let widthPerPeer = windowSize.value.width / videoResolutions.length;
  let heightPerPeer = windowSize.value.height / videoResolutions.length;

  let totalAreaIfRow = 0;
  // calculate area if row
  for (const iterator of videoResolutions) {
    let elementWidth = iterator.width;
    let elementHeight = iterator.height;

    if (elementWidth > widthPerPeer) {
      elementWidth = widthPerPeer;
      elementHeight = elementWidth * (iterator.height / iterator.width);
    }

    if (elementHeight > windowSize.value.height) {
      elementHeight = windowSize.value.height;
      elementWidth = elementHeight * (iterator.width / iterator.height);
    }

    totalAreaIfRow += elementWidth * elementHeight;
  }

  let totalAreaIfColumn = 0;
  // calculate area if column
  for (const iterator of videoResolutions) {
    let elementWidth = iterator.width;
    let elementHeight = iterator.height;

    if (elementHeight > heightPerPeer) {
      elementHeight = heightPerPeer;
      elementWidth = elementHeight * (iterator.width / iterator.height);
    }

    if (elementWidth > windowSize.value.width) {
      elementWidth = windowSize.value.width;
      elementHeight = elementWidth * (iterator.height / iterator.width);
    }

    totalAreaIfColumn += elementWidth * elementHeight;
  }

  if (totalAreaIfRow > totalAreaIfColumn) {
    return "row-reverse";
  } else {
    return "column-reverse";
  }
});

// handle window resize
function handleResize() {
  windowSize.value = {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function resizeConnected() {
  console.log("resize video container, call connected");
  handleResize();
}

window.addEventListener("resize", handleResize);
eventEmitter.on(Events.WebRTCConnected, resizeConnected);

// remove event listener on unmount
onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  eventEmitter.off(Events.WebRTCConnected, resizeConnected);
});
</script>

<template>
  <div class="videos" :style="'  flex-direction: ' + flexDirection + ';'">
    <template v-for="peer in state.peers" :key="peer.peerName">
      <PeerVideo v-bind="props" :peer="peer" v-if="!isPeerHidden(peer)"></PeerVideo>
    </template>
  </div>
</template>

<style>
.videos {
  display: flex;

  flex: 1;
  height: 100%;
  max-height: 100vh;
  justify-content: space-around;
}
</style>
